import { PAYROLL_1405_CONFIG } from "@/config/payroll1405";

export type Payroll1405Input = {
  monthlyBaseSalaryToman: number;
  workDays: number;
  isMarried: boolean;
  childrenCount: number;
  hasSeniority: boolean;
  overtimeHours: number;
  includeHousing: boolean;
  includeGrocery: boolean;
};

export type Payroll1405Result = {
  baseSalaryRial: number;
  seniorityRial: number;
  housingRial: number;
  groceryRial: number;
  maritalRial: number;
  childAllowanceRial: number;
  overtimeRial: number;
  grossRial: number;
  insuranceRial: number;
  taxRial: number;
  totalDeductionsRial: number;
  netRial: number;
};

export const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

export const parseNumber = (value: string) =>
  Number(
    toEnglishDigits(value)
      .replace(/,/g, "")
      .replace(/[^\d.]/g, ""),
  ) || 0;

export const formatInputNumber = (value: string) => {
  const parsed = parseNumber(value);
  return parsed ? parsed.toLocaleString("en-US") : "";
};

export const formatToman = (rial: number) =>
  `${Math.round(rial / 10).toLocaleString("fa-IR")} تومان`;

export const formatRialAsTomanNumber = (rial: number) =>
  Math.round(rial / 10).toLocaleString("fa-IR");

const prorateMonthly = (rial: number, workDays: number) =>
  (rial * Math.min(Math.max(workDays, 0), 30)) / 30;

const calculateTax = (taxableIncomeRial: number) => {
  let previousLimit = 0;
  let tax = 0;

  for (const bracket of PAYROLL_1405_CONFIG.taxBrackets) {
    if (taxableIncomeRial <= previousLimit) break;

    const taxableSlice =
      Math.min(taxableIncomeRial, bracket.upToRial) - previousLimit;
    tax += taxableSlice * bracket.rate;
    previousLimit = bracket.upToRial;
  }

  return Math.max(tax, 0);
};

export const calculatePayroll1405 = (
  input: Payroll1405Input,
): Payroll1405Result | null => {
  const workDays = Math.min(Math.max(Math.floor(input.workDays), 1), 31);
  const monthlyBaseSalaryRial = input.monthlyBaseSalaryToman * 10;

  if (!monthlyBaseSalaryRial) return null;

  const dailyBaseSalaryRial = monthlyBaseSalaryRial / 30;
  const baseSalaryRial = dailyBaseSalaryRial * workDays;
  const seniorityRial = input.hasSeniority
    ? PAYROLL_1405_CONFIG.seniorityDailyRial * workDays
    : 0;
  const housingRial = input.includeHousing
    ? prorateMonthly(PAYROLL_1405_CONFIG.housingAllowanceRial, workDays)
    : 0;
  const groceryRial = input.includeGrocery
    ? prorateMonthly(PAYROLL_1405_CONFIG.groceryAllowanceRial, workDays)
    : 0;
  const maritalRial = input.isMarried
    ? prorateMonthly(PAYROLL_1405_CONFIG.maritalAllowanceRial, workDays)
    : 0;
  const childAllowanceRial =
    Math.max(Math.floor(input.childrenCount), 0) *
    PAYROLL_1405_CONFIG.childAllowanceMultiplier *
    PAYROLL_1405_CONFIG.dailyMinimumWageRial;
  const hourlyWageRial =
    dailyBaseSalaryRial / PAYROLL_1405_CONFIG.standardDailyHours;
  const overtimeRial =
    Math.max(input.overtimeHours, 0) *
    hourlyWageRial *
    PAYROLL_1405_CONFIG.overtimeMultiplier;
  const grossRial =
    baseSalaryRial +
    seniorityRial +
    housingRial +
    groceryRial +
    maritalRial +
    childAllowanceRial +
    overtimeRial;
  const insuranceRial = grossRial * PAYROLL_1405_CONFIG.workerInsuranceRate;
  const taxRial = calculateTax(grossRial);
  const totalDeductionsRial = insuranceRial + taxRial;

  return {
    baseSalaryRial,
    seniorityRial,
    housingRial,
    groceryRial,
    maritalRial,
    childAllowanceRial,
    overtimeRial,
    grossRial,
    insuranceRial,
    taxRial,
    totalDeductionsRial,
    netRial: grossRial - totalDeductionsRial,
  };
};

